"use client";

import { Content } from "@/services/content/types";
import { LIVE_REPORT_LIMIT } from "@/utils/constants";
import { createImageSizes, createImageUrl } from "@/utils/image";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { CustomCategory } from "../category";
import FormatDate from "../format-date";
import Icon from "../icon";
import Item from "./item";

type ClientProps = {
  post?: Content.LiveReportPost;
  article: Content.Article;
  initialItems: Content.LiveReportPost[];
  initialPinnedItems: Content.LiveReportPost[];
  refresh: (limit?: number) => Promise<{
    items: Content.LiveReportPost[];
    pinnedItems: Content.LiveReportPost[];
  }>;
  categoryArticles?: Content.Article[];
};

const Client = (props: ClientProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [allItems, setAllItems] = useState(props.initialItems);
  const [visibleItems, setVisibleItems] = useState(props.initialItems);
  const [pinnedItems, setPinnedItems] = useState(props.initialPinnedItems);
  const [page, setPage] = useState(1);
  const [lastBtoa, setLastBtoa] = useState<string>("");
  const [openPost, setOpenPost] = useState<Content.LiveReportPost | undefined>(
    props.post,
  );
  const postRef = useRef<HTMLDivElement | null>(null);
  const firstRef = useRef<HTMLDivElement | null>(null);
  const search = useSearchParams();
  const pathname = usePathname();

  const refresh = useCallback(async () => {
    const checksum = await fetch(
      `/live-report-post-checksum?articleId=${props.article.sys.id}&limit=${
        page * LIVE_REPORT_LIMIT
      }&preview=${search.get("preview")}`,
    ).then((res) => res.text());

    if (checksum !== lastBtoa) {
      setLastBtoa(checksum);

      try {
        const res = await props.refresh(page * LIVE_REPORT_LIMIT);
        setAllItems(res.items);
        setPinnedItems(res.pinnedItems);

        // If we're at the top of the list, show the new items directly
        if (
          res.items.length > 0 &&
          firstRef.current &&
          firstRef.current.getBoundingClientRect().top > 0
        ) {
          setVisibleItems(res.items);
        }
      } catch (e) {
        // Reload the page if we get an error. This happends when we deploy.
        const lastReload = search.get("r");
        if (!lastReload || parseInt(lastReload) < Date.now() - 10000) {
          window.location.href =
            pathname + "?" + search.toString() + "&r=" + Date.now();
        }
      }
    }
  }, [props, allItems, page]);

  const loadMore = useCallback(async () => {
    const res = await props.refresh((page + 1) * LIVE_REPORT_LIMIT);
    setAllItems(res.items);
    setPinnedItems(res.pinnedItems);
    setVisibleItems(res.items);
    setPage(page + 1);
  }, [props, allItems, page]);

  useEffect(() => {
    if (postRef.current)
      props.post &&
        window.scrollTo({
          top: postRef.current.getBoundingClientRect().top - 150,
          behavior: "smooth",
        });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      refresh();
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [refresh]);

  const newCount = allItems?.filter(
    (i) => i.date > visibleItems[0]?.date,
  ).length;
  return (
    <>
      {openPost && (
        <div
          ref={postRef}
          className="pl-10 p-4 border-2 border-sand-600 relative"
        >
          <button
            className="btn-icon bg-sand-900 absolute top-[-1rem] right-[-1rem] z-10"
            onClick={() => {
              setOpenPost(undefined);
              const url = new URL(window.location.href);
              url.searchParams.delete("post");
              window.history.replaceState({}, "", url.toString());
            }}
          >
            <Icon.Close width={14} height={14} />
          </button>
          <Item post={openPost} article={props.article} />
        </div>
      )}

      {newCount > 0 && (
        <div className="sticky top-40 w-full text-center z-10 pointer-events-none left-0">
          <div
            className={classNames(
              "bg-red-800 transition-all text-sm rounded-sm text-white-500 p-2 px-4 inline-flex gap-2 items-center shadow-lg cursor-pointer",
              {
                "opacity-1 translate-y-0 pointer-events-auto": newCount > 0,
                "opacity-0 -translate-y-10 pointer-events-none": newCount == 0,
              },
            )}
            onClick={() => {
              window.scrollTo({
                top: firstRef.current
                  ? firstRef.current.getBoundingClientRect().top +
                    window.scrollY -
                    250
                  : 0,
                behavior: "smooth",
              });
              setVisibleItems(allItems);
            }}
          >
            Det finns {newCount == 1 ? `ett nytt` : `${newCount} nya`} inlägg{" "}
            <Icon.Up className="size-3" />
          </div>
        </div>
      )}

      {pinnedItems.length > 0 && (
        <div className="bg-sand-700 p-4 rounded-lg">
          <h2 className="font-bold">Missa inte det här</h2>
          {pinnedItems.map((item) => (
            <details key={item.id}>
              <summary className="p-2 cursor-pointer">{item.heading}</summary>
              <div className="p-4 border-l-2 border-sand-900 ml-3">
                {item.jsx}
              </div>
            </details>
          ))}
        </div>
      )}

      <div className="space-y-4 p-4 pr-0">
        {allItems.map((item, i) => {
          const divProps = i === 0 ? { ref: firstRef } : {};
          if (item.origin === "article") {
            return (
              <Link
                className="group flex flex-row space-y-0 space-x-4 border-l-2 border-sand-600 pl-4 py-4"
                href={item.fields.slug!}
                key={item.id || i}
              >
                <div className="mt-0 flex-1">
                  <div className="text-gray-400 ">
                    <FormatDate date={item.date} format="relative" />{" "}
                    <FormatDate date={item.date} format="time" />
                  </div>

                  <div className="flex-1">
                    {item.fields.categories?.[0].fields.color &&
                      item.fields.categories?.[0].fields.name && (
                        <CustomCategory
                          color={item.fields.categories?.[0].fields.color}
                          name={item.fields.categories?.[0].fields.name}
                        />
                      )}
                    {item.fields.title && (
                      <h3 className="text-lg md:text-xl font-semibold group-hover:underline">
                        {item.fields.title}
                      </h3>
                    )}
                  </div>
                </div>
                {item.fields.images?.[0].fields.file?.url && (
                  <div
                    className={classNames(
                      "relative flex-shrink-0 aspect-w-16 aspect-h-9 aspect-none w-[120px] h-[68px] md:w-[180px] md:h-[100px]",
                    )}
                  >
                    <Image
                      fill
                      sizes={createImageSizes({
                        mobile: "12vw",
                        tablet: "12vw",
                        desktop: "12vw",
                      })}
                      src={createImageUrl(
                        item.fields.images?.[0].fields.file.url,
                        {
                          crop: "16:9",
                        },
                      )}
                      alt={item.fields.images?.[0].fields.file.alt || ""}
                      className=""
                    />
                  </div>
                )}
              </Link>
            );
          }

          // Annars kör standardvisning
          return (
            <div key={item.id || i} {...divProps}>
              <Item post={item} article={props.article} />
            </div>
          );
        })}
      </div>

      {allItems.length == page * LIVE_REPORT_LIMIT && (
        <div className="p-4 border-t border-sand-700 flex justify-center">
          <button className="btn" disabled={isLoading} onClick={loadMore}>
            Visa äldre inlägg
          </button>
        </div>
      )}
    </>
  );
};

export default Client;
